import { useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { useAuthState } from "../zustand/AuthState";
import {
  ConnectionStatus,
  useConnectionState,
} from "../zustand/ConnectionState";
import { useNavigationState } from "@/zustand/NavigationState";
import { getChatFromLocalDB } from "@/indexedDB/services/chats";
import { syncChats } from "@/middleware/chatsSync";
import { CHAT_SYNC_FULL_TAKE } from "@/config/constants";
import { useChatListState } from "@/zustand/ChatListState";
import { registerSocketEventHandlers } from "./socketEventHandlers";
import { SocketEventTypes } from "./types";
import { safeSocketHandler } from "./safeSocketHandler";
import { getGetActiveRoomsCallback } from "./callsEventsHandler";
import {
  startActiveRoomsRefresh,
  stopActiveRoomsRefresh,
} from "./activeRoomsRefresh";
import { featureFlags } from "@/config/featureFlags";
import { createLogger } from "@/utils/debug";
import {
  generateWebPushSubscription,
  getStoredSubscription,
  isWebPushSubscribed,
  PUBLIC_KEY_TIMEOUT_MS,
  waitForWebPushPublicKey,
} from "@/components/web-push/webPushUtils";
import { isUserAuthenticated } from "@/middleware/auth";
import { isAppVisible } from "@/utils/visibilityState";
import { addBreadcrumb } from "@/components/error-boundary/breadcrumbs";
import { syncEpochsForChat } from "@/middleware/epochKeyCache";
import { db } from "@/indexedDB/db";
import { ChatType } from "@/server/types/chat";

// Create module-specific logger
const log = createLogger("Socket");

/**
 * Socket.IO connection management
 *
 * These variables and functions maintain the singleton socket connection state
 * across the application. They exist outside the component lifecycle to ensure
 * connection persistence.
 */

// Singleton socket instance to ensure only one connection exists application-wide
let globalSocketInstance: Socket | null = null;

// Track the current auth token used for the socket connection
// This helps us determine when the token changes and we need to reconnect
let currentSocketAuthToken: string | null = null;

// Heartbeat interval reference
let heartbeatInterval: NodeJS.Timeout | null = null;

// Timestamp of the last successful heartbeat or connect event.
// Initialized to now so the first visibilitychange/focus after page load
// doesn't spuriously force a reconnect before the socket has connected.
let lastSocketActivityAt = Date.now();

// Heartbeat configuration
const HEARTBEAT_INTERVAL = 20000; // 20 seconds

// If the socket has been idle longer than this after app resume, force reconnect
const STALE_SOCKET_THRESHOLD_MS = HEARTBEAT_INTERVAL * 2 + 5000;

/**
 * Creates a new Socket.IO instance with the provided token
 *
 * @param token - Authentication token for the socket connection
 * @returns A configured Socket.IO instance (not yet connected)
 */
const createSocketInstance = (token: string): Socket => {
  return io(import.meta.env.VITE_WS_BROKER_URL, {
    transports: ["websocket"], // Prefer WebSocket transport
    path: "/broker/socket.io", // set handshake path
    auth: {
      token,
    },
    autoConnect: true,
    reconnection: true, // Enable automatic reconnection
    reconnectionAttempts: 10, // Retry indefinitely (or set a max)
    reconnectionDelay: 1000, // Start with a 1-second delay
    reconnectionDelayMax: 5000, // Cap delay at 5 seconds
    timeout: 20000, // Connection timeout
  });
};

/**
 * Starts the heartbeat mechanism
 */
const startHeartbeat = (socket: Socket): void => {
  // Clear any existing heartbeat
  stopHeartbeat();

  log.info("Starting heartbeat mechanism");

  heartbeatInterval = setInterval(() => {
    if (socket.connected) {
      const heartbeatData = {
        timestamp: Date.now(),
        clientId: socket.id,
      };

      log.info("Sending heartbeat", heartbeatData);

      // Fire-and-forget - no acknowledgment expected
      socket.emit(SocketEventTypes.HEARTBEAT, heartbeatData);
      lastSocketActivityAt = Date.now();
    } else {
      log.warn("Socket not connected, skipping heartbeat");
    }
  }, HEARTBEAT_INTERVAL);
};

/**
 * Stops the heartbeat mechanism
 */
const stopHeartbeat = (): void => {
  if (heartbeatInterval) {
    log.info("Stopping heartbeat mechanism");
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
};

/**
 * Handle Web Push Subscriptions
 */
const handleWebPushSubs = async (socket: Socket): Promise<void> => {
  log.info("Syncing Web Push Subscriptions");

  const userFingerPrint = isUserAuthenticated();

  if (!userFingerPrint || !socket.active) {
    return;
  }

  // if user has explicitly opted out, don't re-subscribe
  if (!isWebPushSubscribed(userFingerPrint.username)) {
    return;
  }

  // check if we have a locally stored subscription for this device
  const storedSub = getStoredSubscription(userFingerPrint.username);

  if (storedSub) {
    // emit subs and wait for response
    socket.emit(
      SocketEventTypes.READ_SUBSCRIPTIONS,
      (subs: PushSubscription[]) => {
        void (async () => {
          try {
            // check if server subs contain stored sub
            // TO DO => check if it is ok to compare by endpoint
            const isStoredSubOnServer = subs.some(
              (serverSub) => serverSub.endpoint === storedSub.endpoint,
            );

            if (isStoredSubOnServer) {
              // subscription exists on server - do nothing
              log.info("Stored subscription found on server");
            } else {
              // generate new subscription
              const key = await waitForWebPushPublicKey(
                socket,
                PUBLIC_KEY_TIMEOUT_MS,
              );
              await generateWebPushSubscription(
                key,
                socket,
                userFingerPrint.username,
              );
            }
          } catch (error) {
            log.error("Failed to sync web push subscription", error);
          }
        })();
      },
    );
  } else {
    // user preference says subscribed but subscription data is missing — re-subscribe
    try {
      const key = await waitForWebPushPublicKey(socket, PUBLIC_KEY_TIMEOUT_MS);
      await generateWebPushSubscription(key, socket, userFingerPrint.username);
    } catch (error) {
      log.error("Failed to generate new web push subscription", error);
    }
  }
};

/**
 * Disconnects and cleans up the global socket instance.
 *
 * Exported for the account-deletion teardown: the useSocket effect's
 * token-change path early-returns when the token clears (before reaching its
 * own cleanup call), so after logout the old socket would otherwise stay
 * registered on the deleted account's token until a new token arrives.
 */
export const cleanupSocketInstance = (): void => {
  if (globalSocketInstance) {
    log.info("Cleaning up existing socket connection");

    stopHeartbeat();
    stopActiveRoomsRefresh();
    globalSocketInstance.disconnect();
    globalSocketInstance.removeAllListeners();
    globalSocketInstance = null;
    currentSocketAuthToken = null;
  }
};

type DebugSocketController = {
  enable: () => void;
  disable: () => void;
  connect: () => void;
  disconnect: () => void;
  getInstance: () => Socket | null;
  startHeartbeat: () => void;
  stopHeartbeat: () => void;
};

declare global {
  interface Window {
    orionSocket?: DebugSocketController;
  }
}

const attachSocketDebugControls = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  const enable = (): void => {
    if (!globalSocketInstance) {
      log.info("Socket debug: no global instance to enable");
      return;
    }

    globalSocketInstance.connect();
  };

  const disable = (): void => {
    if (!globalSocketInstance) {
      log.info("Socket debug: no global instance to disable");
      return;
    }

    globalSocketInstance.disconnect();
  };

  const connect = (): void => {
    enable();
  };

  const disconnect = (): void => {
    disable();
  };

  window.orionSocket = {
    enable,
    disable,
    connect,
    disconnect,
    getInstance: () => globalSocketInstance,
    startHeartbeat: () => {
      if (globalSocketInstance) {
        startHeartbeat(globalSocketInstance);
      }
    },
    stopHeartbeat,
  };
};

attachSocketDebugControls();

/**
 * When the app resumes from background (iOS PWA especially), the WebSocket
 * may be dead even though socket.connected still reads true. Check staleness
 * and force a clean reconnect if needed.
 */
const handleVisibilityResume = (): void => {
  if (!isAppVisible() || !globalSocketInstance) {
    return;
  }

  const elapsed = Date.now() - lastSocketActivityAt;
  const isStale = elapsed > STALE_SOCKET_THRESHOLD_MS;

  if (!globalSocketInstance.connected || isStale) {
    log.info("App resumed with stale/dead socket, forcing reconnect", {
      connected: globalSocketInstance.connected,
      elapsed,
    });
    globalSocketInstance.disconnect();
    globalSocketInstance.connect();
  }
};

if (typeof document !== "undefined") {
  // iOS doesn't always fire visibilitychange reliably when resuming a PWA —
  // pageshow and focus cover the remaining resume paths.
  document.addEventListener("visibilitychange", handleVisibilityResume);
  globalThis.addEventListener("pageshow", handleVisibilityResume);
  globalThis.addEventListener("focus", handleVisibilityResume);
}

/**
 * Custom hook for managing WebSocket connection using Socket.IO
 *
 * Handles connection setup, event registration, and cleanup
 * Ensures only one active connection exists at a time using a singleton pattern
 * Properly manages reconnection and connection status
 *
 * @returns The current Socket instance or null if not connected
 */
export const useSocket = (): Socket | null => {
  const tk = useAuthState((state) => state.tk);
  const socketInitializedRef = useRef<boolean>(false);

  const setSocket = useConnectionState((state) => state.setSocket);
  const setWsConnectionStatus = useConnectionState(
    (state) => state.setWsConnectionStatus,
  );
  const socket = useConnectionState((state) => state.socket);

  useEffect(() => {
    // Force reconnection if token changes (e.g., after login)
    if (tk && globalSocketInstance) {
      if (currentSocketAuthToken !== tk) {
        log.info("Auth token changed, reconnecting socket");

        cleanupSocketInstance();
      }
    }

    // Skip initialization if we already have a valid socket
    if (globalSocketInstance?.connected && socketInitializedRef.current) {
      log.info("Using existing socket connection");

      return;
    }

    // Don't initialize if no token is available
    if (!tk) {
      log.info("No auth token available, skipping socket connection");

      return;
    }

    // Clean up existing socket before creating a new one
    cleanupSocketInstance();

    // Create new socket instance
    const socketIo = createSocketInstance(tk);

    // Register event listeners — wrapped to prevent unhandled throws
    socketIo.on(
      "connect",
      safeSocketHandler("connect", async () => {
        const setLastOnlineTimestamp =
          useConnectionState.getState().setLastOnlineTimestamp;
        const activeChatId = useNavigationState.getState().activeChatId;

        // web push logic
        handleWebPushSubs(socketIo);

        // Mark activity and start heartbeat on connection
        lastSocketActivityAt = Date.now();
        startHeartbeat(socketIo);

        // Gated: new sync engine handles event reconciliation
        if (!featureFlags.useNewSync) {
          // TO DO => temporary => extract to global handler
          // check if any active chat ID and sync events
          if (activeChatId) {
            const chatDetails = await getChatFromLocalDB(activeChatId);
            if (chatDetails) {
              socketIo.emit(SocketEventTypes.FETCH_EVENTS, {
                chat_id: activeChatId,
                timestamp: chatDetails.lastSyncedAt,
              });
            }
          }
        }

        setWsConnectionStatus(ConnectionStatus.CONNECTED);
        addBreadcrumb("socket", "connected");

        // set new online timestamp
        setLastOnlineTimestamp(new Date().toISOString());

        socketIo.emit(
          SocketEventTypes.GET_ACTIVE_ROOMS,
          getGetActiveRoomsCallback,
        );

        // Keep reconciling activeCallIds against server truth while this
        // socket stays connected (self-heals a ghost call within one TTL
        // window even if a `meeting-ended` push is missed and the socket
        // never reconnects — see call-status-rca.md). Restarting on every
        // connect is safe: it always clears any prior interval first, so
        // reconnects never stack multiple intervals.
        startActiveRoomsRefresh(socketIo);

        // When the new sync engine is active, runSync() handles both initial
        // load and reconnect via the useSync hook. Skip the legacy path to
        // avoid double chat-list fetches.
        if (!featureFlags.useNewSync) {
          await syncChats({
            take: CHAT_SYNC_FULL_TAKE,
            skip: 0,
          });
        }

        // Bulk epoch catch-up (spec §11.2, Option A). After chats are synced
        // with server-side currentEpoch, sync epoch keys for group chats that
        // may have rotated while we were offline.
        try {
          const allChats = await db.chats.toArray();
          const groupChats = allChats
            .filter((c) => c.type === ChatType.GROUP)
            .filter((c) => (c.currentEpoch ?? 0) > 0);
          // Bounded parallelism so we don't open hundreds of HTTPS connections
          // at once, but also don't serialize across all chats.
          const CONCURRENCY = 8;
          for (let i = 0; i < groupChats.length; i += CONCURRENCY) {
            const batch = groupChats.slice(i, i + CONCURRENCY);
            await Promise.allSettled(
              batch.map((chat) =>
                syncEpochsForChat(chat.id, chat.currentEpoch ?? 0),
              ),
            );
          }
        } catch (epochSyncError) {
          log.warn("Epoch sync on reconnect failed", epochSyncError);
        }
      }),
    );

    socketIo.on(
      "disconnect",
      safeSocketHandler("disconnect", () => {
        const setLastOfflineTimestamp =
          useConnectionState.getState().setLastOfflineTimestamp;
        const setChatListSyncing =
          useChatListState.getState().setChatListSyncing;

        // Stop heartbeat on disconnect
        stopHeartbeat();
        stopActiveRoomsRefresh();

        setWsConnectionStatus(ConnectionStatus.DISCONNECTED);
        addBreadcrumb("socket", "disconnected");

        // reset chat list syncing state
        setChatListSyncing(false);

        // set new offline timestamp
        setLastOfflineTimestamp(new Date().toISOString());
      }),
    );

    // Register all event handlers for the socket
    registerSocketEventHandlers(socketIo, setWsConnectionStatus);

    // Set global instance and update state
    globalSocketInstance = socketIo;
    currentSocketAuthToken = tk;
    socketInitializedRef.current = true;
    setSocket(socketIo);

    // Connect manually to ensure we have control over the connection
    socketIo.connect();

    /**
     * Cleanup function - only marks the component as not using the socket
     *
     * We intentionally don't disconnect or remove listeners here to maintain
     * the singleton connection across component remounts. This ensures that
     * the WebSocket connection persists even when components using it unmount.
     *
     * The actual socket cleanup happens only when:
     * 1. The auth token changes (e.g., on login/logout)
     * 2. The application is fully unmounted
     */
    return () => {
      socketInitializedRef.current = false;
    };
  }, [setWsConnectionStatus, setSocket, tk]); // Effect depends on auth token to reconnect when it changes

  // We don't need a separate cleanup effect - the socket is managed globally
  // and will persist across component unmounts

  return socket;
};
